/**
 * お問合せ画面コンポーネント
 */
import { useState } from "react";
import { Send, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { Reveal, PageHeader } from "../components/ui.tsx";

const FORM_ENDPOINT = "https://formspree.io/f/mdkzeqad";

const inputClass =
  "w-full border border-slate-200 bg-slate-50 rounded-xl px-4 py-3 text-slate-800 placeholder-slate-400 transition focus:outline-none focus:bg-white focus:border-brand-500 focus:ring-4 focus:ring-brand-500/10";

const ContactPage = () => {
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        message: "",
    });

    const [submitted, setSubmitted] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    // ページ遷移させず、fetch で Formspree に送信して自サイト内で完了表示する
    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setSending(true);
        setError(false);
        try {
            const res = await fetch(FORM_ENDPOINT, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
                body: JSON.stringify(formData),
            });
            if (res.ok) {
                setSubmitted(true);
            } else {
                setError(true);
            }
        } catch {
            setError(true);
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="bg-white">
            <PageHeader
                en="Contact"
                ja="お問い合わせ"
                lead="ご質問・ご相談などございましたら、下記フォームよりお気軽にお問い合わせください。"
            />

            <section className="py-20 md:py-24">
                <div className="max-w-2xl mx-auto px-6">
                    {submitted ? (
                        <Reveal>
                            <div className="rounded-2xl border border-slate-200 bg-white p-10 md:p-12 text-center">
                                <CheckCircle2
                                    size={56}
                                    strokeWidth={1.5}
                                    className="mx-auto mb-6 text-brand-500"
                                />
                                <h2 className="text-2xl font-bold text-ink mb-4">
                                    送信が完了しました
                                </h2>
                                <p className="text-slate-500 leading-relaxed">
                                    お問い合わせいただきありがとうございます。
                                    <br />
                                    内容を確認のうえ、担当者よりご連絡いたします。
                                </p>
                            </div>
                        </Reveal>
                    ) : (
                        <Reveal>
                            <form
                                onSubmit={handleSubmit}
                                className="rounded-2xl border border-slate-200 bg-white p-8 md:p-10 space-y-7"
                            >
                                <div>
                                    <label htmlFor="name" className="block mb-2 text-sm font-semibold text-ink">
                                        お名前 <span className="text-brand-600">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        id="name"
                                        required
                                        placeholder="山田 太郎"
                                        value={formData.name}
                                        onChange={handleChange}
                                        className={inputClass}
                                    />
                                </div>

                                <div>
                                    <label htmlFor="email" className="block mb-2 text-sm font-semibold text-ink">
                                        メールアドレス <span className="text-brand-600">*</span>
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        id="email"
                                        required
                                        placeholder="example@email.com"
                                        value={formData.email}
                                        onChange={handleChange}
                                        className={inputClass}
                                    />
                                </div>

                                <div>
                                    <label htmlFor="message" className="block mb-2 text-sm font-semibold text-ink">
                                        お問い合わせ内容 <span className="text-brand-600">*</span>
                                    </label>
                                    <textarea
                                        name="message"
                                        id="message"
                                        required
                                        rows={6}
                                        placeholder="お問い合わせ内容をご記入ください"
                                        value={formData.message}
                                        onChange={handleChange}
                                        className={inputClass}
                                    />
                                </div>

                                {error && (
                                    <div className="flex items-start gap-3 rounded-xl bg-red-50 border border-red-100 px-4 py-3 text-sm text-red-700">
                                        <AlertCircle size={18} className="shrink-0 mt-0.5" />
                                        <p>
                                            送信に失敗しました。時間をおいて再度お試しください。
                                        </p>
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={sending}
                                    className="group w-full inline-flex items-center justify-center gap-2 bg-ink hover:bg-brand-600 disabled:opacity-60 disabled:pointer-events-none text-white font-semibold px-6 py-4 rounded-full transition-colors"
                                >
                                    {sending ? (
                                        <>
                                            送信中…
                                            <Loader2 size={16} className="animate-spin" />
                                        </>
                                    ) : (
                                        <>
                                            送信する
                                            <Send size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                        </>
                                    )}
                                </button>
                            </form>
                        </Reveal>
                    )}
                </div>
            </section>
        </div>
    );
};

export default ContactPage;
